import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  afterNextRender,
  inject,
  input,
  viewChild,
} from '@angular/core';

const SLICE_COUNT = 64;
const RING_COUNT = 64;
const PARTICLE_RADIUS = 0.005;
/** Dots smaller than this (in CSS px) are invisible, so they are not drawn at all. */
const MIN_DOT_RADIUS = 0.05;
const DEGREES_PER_HALF_TURN = 180;
const DEG = Math.PI / DEGREES_PER_HALF_TURN;
const CAMERA_DISTANCE = 5;
/** Focal length of a perspective camera with a 24° vertical field of view. */
const HALF_FIELD_OF_VIEW_DEG = 12;
const FOCAL = 1 / Math.tan(HALF_FIELD_OF_VIEW_DEG * DEG);
const MAX_PIXEL_RATIO = 2;
/** Entries of a 3×3 rotation matrix. */
const MATRIX_3X3_SIZE = 9;
/** Initial Y and Z tilt of a slice, as multiples of its X tilt. */
const SLICE_TILT_Y_FACTOR = 3;
const SLICE_TILT_Z_FACTOR = 6;
/** Per-frame spin of a slice around each axis (radians): a base plus a step per slice index. */
const SLICE_SPIN_X_BASE = 0.001;
const SLICE_SPIN_X_STEP = 0.0002;
const SLICE_SPIN_Y_BASE = 0.0015;
const SLICE_SPIN_Y_STEP = 0.0001;
const SLICE_SPIN_Z_BASE = 0.002;
const SLICE_SPIN_Z_STEP = 0.0002;
/** Radians per frame of the whole group's wobble. */
const GROUP_WOBBLE_SPEED = 0.01;
/** Per-frame advance of the drawing phase. */
const PHASE_PER_FRAME = 0.00001;

interface Rotation {
  x: number;
  y: number;
  z: number;
}

function ringTable(trig: (angle: number) => number): Float64Array {
  const table = new Float64Array(RING_COUNT);
  for (let iy = 0; iy < RING_COUNT; ++iy) {
    table[iy] = trig(iy * (2 / RING_COUNT) * Math.PI);
  }
  return table;
}

/** Ring positions are identical for every slice and every instance. */
const RING_X = ringTable(Math.sin);
const RING_Y = ringTable(Math.cos);

function initialSliceRotations(): Rotation[] {
  const rotations: Rotation[] = [];
  for (let ix = 0; ix < SLICE_COUNT; ++ix) {
    const baseRotation = (ix / SLICE_COUNT) * DEGREES_PER_HALF_TURN * DEG;
    rotations.push({
      x: baseRotation,
      y: baseRotation * SLICE_TILT_Y_FACTOR,
      z: baseRotation * SLICE_TILT_Z_FACTOR,
    });
  }
  return rotations;
}

/**
 * Writes the rotation matrix of XYZ-order Euler angles into `out` column by column:
 * [c0x, c0y, c0z, c1x, c1y, c1z, c2x, c2y, c2z].
 */
function rotationColumns(rotation: Rotation, out: Float64Array): void {
  const a = Math.cos(rotation.x);
  const b = Math.sin(rotation.x);
  const c = Math.cos(rotation.y);
  const d = Math.sin(rotation.y);
  const e = Math.cos(rotation.z);
  const f = Math.sin(rotation.z);

  out[0] = c * e;
  out[1] = a * f + b * e * d;
  out[2] = b * f - a * e * d;
  out[3] = -c * f;
  out[4] = a * e - b * f * d;
  out[5] = b * e + a * f * d;
  out[6] = d;
  out[7] = -b * c;
  out[8] = a * c;
}

@Component({
  selector: 'fr8bot-ui-particle-sphere',
  templateUrl: './particle-sphere.component.html',
  styleUrl: './particle-sphere.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ParticleSphereUIComponent {
  readonly color = input('#c1d91c');

  private readonly canvasRef = viewChild.required<ElementRef<HTMLCanvasElement>>('canvas');

  private context: CanvasRenderingContext2D | null = null;
  private width = 0;
  private height = 0;
  private pixelRatio = 0;

  private animationId: number | null = null;
  private framesCount = 0;

  private resizeObserver: ResizeObserver | null = null;
  private intersectionObserver: IntersectionObserver | null = null;

  private readonly sliceRotations = initialSliceRotations();
  private readonly groupRotation: Rotation = { x: 0, y: 0, z: 0 };
  private readonly sliceMatrix = new Float64Array(MATRIX_3X3_SIZE);
  private readonly groupMatrix = new Float64Array(MATRIX_3X3_SIZE);

  constructor() {
    afterNextRender(() => {
      this.context = this.canvasRef().nativeElement.getContext('2d');
      if (!this.context) return;
      // The resize observer's first callback sizes the canvas.
      this.initObservers();
      this.startLoop();
    });

    inject(DestroyRef).onDestroy(() => this.teardown());
  }

  private startLoop(): void {
    const loop = () => {
      this.animationId = requestAnimationFrame(loop);
      this.advance();
      this.draw();
    };

    loop();
  }

  private stopLoop(): void {
    if (this.animationId !== null) {
      cancelAnimationFrame(this.animationId);
      this.animationId = null;
    }
  }

  private advance(): void {
    this.framesCount++;

    for (let ix = 0; ix < SLICE_COUNT; ++ix) {
      const rotation = this.sliceRotations[ix]!;
      rotation.x += SLICE_SPIN_X_BASE + SLICE_SPIN_X_STEP * ix;
      rotation.y += SLICE_SPIN_Y_BASE + SLICE_SPIN_Y_STEP * ix;
      rotation.z += SLICE_SPIN_Z_BASE + SLICE_SPIN_Z_STEP * ix;
    }

    this.groupRotation.y = Math.cos(this.framesCount * GROUP_WOBBLE_SPEED);
    this.groupRotation.z = Math.sin(this.framesCount * GROUP_WOBBLE_SPEED);
  }

  private draw(): void {
    const ctx = this.context;
    if (!ctx || this.width === 0 || this.height === 0) return;

    const phase = this.framesCount * PHASE_PER_FRAME;
    const r = this.sliceMatrix;
    const g = this.groupMatrix;
    const centerX = this.width / 2;
    const centerY = this.height / 2;
    // Pixels per world unit at the camera's focal plane.
    const projection = FOCAL * centerY;

    rotationColumns(this.groupRotation, g);

    // Set every frame: a lost and restored 2D context comes back with the identity transform.
    ctx.setTransform(this.pixelRatio, 0, 0, this.pixelRatio, 0, 0);
    ctx.clearRect(0, 0, this.width, this.height);
    ctx.fillStyle = this.color();
    ctx.beginPath();

    for (let ix = 0; ix < SLICE_COUNT; ++ix) {
      rotationColumns(this.sliceRotations[ix]!, r);

      for (let iy = 0; iy < RING_COUNT; ++iy) {
        const i = ix * RING_COUNT + iy;
        const px = RING_X[iy]!;
        const py = RING_Y[iy]!;

        // Ring point rotated by its slice, then by the whole group.
        const lx = r[0]! * px + r[3]! * py;
        const ly = r[1]! * px + r[4]! * py;
        const lz = r[2]! * px + r[5]! * py;
        const wx = g[0]! * lx + g[3]! * ly + g[6]! * lz;
        const wy = g[1]! * lx + g[4]! * ly + g[7]! * lz;
        const wz = g[2]! * lx + g[5]! * ly + g[8]! * lz;

        const perspective = projection / (CAMERA_DISTANCE - wz);
        const radius = PARTICLE_RADIUS * Math.abs(Math.sin(phase * i)) * perspective;
        if (radius < MIN_DOT_RADIUS) continue;

        const sx = centerX + wx * perspective;
        const sy = centerY - wy * perspective;
        ctx.moveTo(sx + radius, sy);
        ctx.arc(sx, sy, radius, 0, 2 * Math.PI);
      }
    }

    ctx.fill();
  }

  private initObservers(): void {
    // The canvas is absolutely sized to the host, so its backing store never feeds back into
    // the size observed here.
    const canvas = this.canvasRef().nativeElement;

    this.resizeObserver = new ResizeObserver(([entry]) => {
      if (!entry) return;
      const { width, height } = entry.contentRect;
      this.updateCanvasSize(width, height);
    });
    this.resizeObserver.observe(canvas);

    // Nothing to animate while the element is scrolled out of view.
    this.intersectionObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;
      if (!entry.isIntersecting) {
        this.stopLoop();
      } else if (this.animationId === null) {
        this.startLoop();
      }
    });
    this.intersectionObserver.observe(canvas);
  }

  private updateCanvasSize(width: number, height: number): void {
    if (!this.context || width === 0 || height === 0) return;

    const pixelRatio = Math.min(window.devicePixelRatio, MAX_PIXEL_RATIO);
    if (width === this.width && height === this.height && pixelRatio === this.pixelRatio) return;

    // Resizing the backing store clears it, and resize observers run after this frame's
    // draw, so redraw here or the frame paints empty.
    const canvas = this.context.canvas;
    canvas.width = Math.round(width * pixelRatio);
    canvas.height = Math.round(height * pixelRatio);
    this.width = width;
    this.height = height;
    this.pixelRatio = pixelRatio;
    this.draw();
  }

  private teardown(): void {
    this.stopLoop();

    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
    this.intersectionObserver?.disconnect();
    this.intersectionObserver = null;

    this.context = null;
  }
}
